import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import {
  BarChart3, RefreshCw, Package, DollarSign, Trash2, TrendingUp, CheckCircle, LayoutDashboard, FileText 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useDashboardStats, PERIOD_LABELS 
} from '@/features/reports';
import type { ReportPeriod } from '@/features/reports';

const DEFAULT_SITE_ID = 'site-celebration';

const TABS = [
  {
    id: 'dashboards',
    label: 'Dashboards',
    href: '/reports/dashboards',
    icon: LayoutDashboard 
  },
  {
    id: 'kpis',
    label: 'KPIs',
    href: '/reports/kpis',
    icon: TrendingUp 
  },
  {
    id: 'compliance',
    label: 'Compliance',
    href: '/reports/compliance',
    icon: FileText 
  },
];

const PERIOD_TABS: ReportPeriod[] = ['today', 'week', 'month', 'quarter'];

export function ReportsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState<ReportPeriod>('today');

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/kpis')) return 'kpis';
    if (location.pathname.includes('/compliance')) return 'compliance';
    return 'dashboards';
  }, [location.pathname]);

  const {
    data, isLoading, error, refetch, isFetching 
  } = useDashboardStats({
    siteId: DEFAULT_SITE_ID,
    period: selectedPeriod 
  });

  const stats = data?.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Reports & Analytics</h1>
          <p className="text-muted-foreground">Operational dashboards and KPI tracking</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex flex-wrap gap-2">
        {PERIOD_TABS.map((period) => (
          <Button key={period} variant={selectedPeriod === period ? 'default' : 'outline'} size="sm" onClick={() => setSelectedPeriod(period)}>
            {PERIOD_LABELS[period]}
          </Button>
        ))}
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={`skel-${i.toString()}`} className="h-32 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="mb-4 h-12 w-12 text-red-500" />
            <p className="text-lg font-medium">Failed to load reports</p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && stats && activeTab === 'compliance' && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">HACCP Compliance</CardTitle>
                <CheckCircle className="h-5 w-5 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">98%</div>
                <p className="text-xs text-muted-foreground">All critical points monitored</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Allergen Tracking</CardTitle>
                <FileText className="h-5 w-5 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">100%</div>
                <p className="text-xs text-muted-foreground">All recipes documented</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Traceability</CardTitle>
                <Package className="h-5 w-5 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">95%</div>
                <p className="text-xs text-muted-foreground">Lot tracking coverage</p>
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader><CardTitle>Compliance Checklist</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  {
                    item: 'Temperature logs complete',
                    status: 'pass' 
                  },
                  {
                    item: 'Supplier certifications current',
                    status: 'pass' 
                  },
                  {
                    item: 'Staff training up to date',
                    status: 'pass' 
                  },
                  {
                    item: 'Equipment calibration',
                    status: 'warning' 
                  },
                  {
                    item: 'Pest control inspection',
                    status: 'pass' 
                  },
                ].map((check) => (
                  <div key={check.item} className="flex items-center justify-between py-2 border-b last:border-0">
                    <span>{check.item}</span>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${check.status === 'pass' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                      {check.status === 'pass' ? 'Compliant' : 'Review Needed'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!isLoading && !error && stats && activeTab === 'kpis' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Food Cost %</CardTitle>
              <DollarSign className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.kpis.foodCostPercentage}%</div>
              <p className="text-xs text-muted-foreground">Target: 28-35%</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Waste %</CardTitle>
              <Trash2 className="h-5 w-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.kpis.wastePercentage}%</div>
              <p className="text-xs text-muted-foreground">Target: &lt;3%</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Forecast Accuracy</CardTitle>
              <TrendingUp className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.kpis.forecastAccuracy}%</div>
              <p className="text-xs text-muted-foreground">Target: &gt;90%</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Order Fill Rate</CardTitle>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.kpis.orderFillRate}%</div>
              <p className="text-xs text-muted-foreground">Target: &gt;95%</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Inventory Turnover</CardTitle>
              <Package className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.kpis.inventoryTurnover}x</div>
              <p className="text-xs text-muted-foreground">Per month</p>
            </CardContent>
          </Card>
        </div>
      )}

      {!isLoading && !error && stats && activeTab === 'dashboards' && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Food Cost %</CardTitle>
                <DollarSign className="h-5 w-5 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.kpis.foodCostPercentage}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Waste %</CardTitle>
                <Trash2 className="h-5 w-5 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.kpis.wastePercentage}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Forecast Accuracy</CardTitle>
                <TrendingUp className="h-5 w-5 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.kpis.forecastAccuracy}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Order Fill Rate</CardTitle>
                <CheckCircle className="h-5 w-5 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.kpis.orderFillRate}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Inventory Turnover</CardTitle>
                <Package className="h-5 w-5 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.kpis.inventoryTurnover}x</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader><CardTitle className="text-lg">Inventory Overview</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Total Items</span><span className="font-medium">{stats.inventory.totalItems}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Low Stock Items</span><span className="font-medium text-amber-600">{stats.inventory.lowStockItems}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Expiring Soon</span><span className="font-medium text-red-600">{stats.inventory.expiringItems}</span></div>
                <div className="flex justify-between border-t pt-2"><span className="text-muted-foreground">Total Value</span><span className="font-bold">${stats.inventory.totalValue.toLocaleString()}</span></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg">Procurement Status</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Pending POs</span><span className="font-medium text-amber-600">{stats.procurement.pendingPOs}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">In Transit</span><span className="font-medium text-blue-600">{stats.procurement.inTransitPOs}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">PO Value</span><span className="font-medium">${stats.procurement.totalPOValue.toLocaleString()}</span></div>
                <div className="flex justify-between border-t pt-2"><span className="text-muted-foreground">Pending Invoices</span><span className="font-bold">${stats.procurement.pendingInvoiceValue.toLocaleString()}</span></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg">Waste Analysis</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Total Logs</span><span className="font-medium">{stats.waste.totalLogs}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Pre-Consumer</span><span className="font-medium text-amber-600">${stats.waste.preConsumerCost.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Post-Consumer</span><span className="font-medium text-purple-600">${stats.waste.postConsumerCost.toFixed(2)}</span></div>
                <div className="flex justify-between border-t pt-2"><span className="text-muted-foreground">Total Cost</span><span className="font-bold text-red-600">${stats.waste.totalCost.toFixed(2)}</span></div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
